// src/components/HomePage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { PublicPage, Eyebrow, FeatureGridSection, DecorLines } from './public/PublicLayout';
import HeroLogin from './public/HeroLogin';

const HomePage = () => {
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginMode, setLoginMode] = useState('carrier'); // 'carrier' or 'dealer'
  const [pendingApproval, setPendingApproval] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setPendingApproval(false);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);

      // Check if user is pending approval
      const userRef = doc(db, 'users', userCredential.user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists() && userSnap.data().active === false) {
        // User exists but not approved yet - sign them out and show message
        await signOut(auth);
        setPendingApproval(true);
        setPendingEmail(loginForm.email);
      }
      // If active, App.js will handle the redirect

    } catch (error) {
      setError('Wrong email or password.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setLoginForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // --- Pending Approval Screen ---
  if (pendingApproval) {
    return (
      <PublicPage>
        <div className="flex items-center justify-center px-4 py-16 sm:py-24">
          <div className="pub-surface pub-line border max-w-lg w-full p-8 rounded-2xl shadow-2xl shadow-black/30 text-center">
            <div className="pub-warn mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-6">
              <i className="fas fa-clock text-4xl"></i>
            </div>

            <h2 className="pub-text text-3xl font-extrabold mb-2">Your account is waiting for approval</h2>
            <p className="pub-muted text-lg mb-6">
              Signed in as <span className="pub-link font-semibold">{pendingEmail}</span>
            </p>

            <div className="pub-surface-2 pub-line border p-6 rounded-xl text-left mb-6">
              <p className="pub-text font-semibold mb-2">What happens next</p>
              <p className="pub-muted text-sm mb-3">
                An admin reviews new accounts within <strong className="pub-text">1–2 business days</strong>.
              </p>
              <p className="pub-muted text-sm">We'll email you when your account is active.</p>
            </div>

            <div className="pub-surface-2 pub-line border p-6 rounded-xl text-left mb-6">
              <p className="pub-text font-semibold mb-3">Need it sooner? Contact us</p>
              <div className="space-y-3">
                <a href="mailto:admin@loadmemo.com" className="pub-link flex items-center">
                  <i className="fas fa-envelope mr-3 w-5"></i>
                  <span>admin@loadmemo.com</span>
                </a>
                <a href="tel:+12015265176" className="pub-link flex items-center">
                  <i className="fas fa-phone mr-3 w-5"></i>
                  <span>(201) 526-5176</span>
                </a>
              </div>
            </div>

            <button
              onClick={() => setPendingApproval(false)}
              className="w-full py-3 rounded-lg pub-btn font-semibold transition"
            >
              <i className="fas fa-arrow-left mr-2"></i>Back to sign in
            </button>
          </div>
        </div>
      </PublicPage>
    );
  }

  return (
    <PublicPage>
      <HeroLogin
        form={loginForm}
        onChange={handleInputChange}
        onSubmit={handleLogin}
        isLoading={isLoading}
        error={error}
        mode={loginMode}
        setMode={setLoginMode}
      />

      {/* ===== Dealer LiveLoad (design 2) ===== */}
      <section id="dealers" className="grid lg:grid-cols-[1.8fr_1fr] border-t border-white/10">
        <div className="relative overflow-hidden pub-band px-6 sm:px-12 lg:px-20 py-16 lg:py-20">
          <DecorLines />
          <div className="relative">
            <Eyebrow>LiveLoad for dealers</Eyebrow>
            <h2 className="mt-6 text-5xl sm:text-7xl font-extrabold tracking-tight leading-[0.95] text-white">
              Your cars.
              <span className="block pub-accent">Your next move.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg sm:text-2xl text-slate-200 leading-snug">
              Upload your gate pass, compare carrier bids and book transport in one place.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-6">
              <span className="flex items-center gap-4 text-white sm:text-lg">
                <span className="w-12 h-12 rounded-lg border-2 pub-accent-border flex items-center justify-center">
                  <i className="fas fa-file-pdf text-xl pub-accent" />
                </span>
                Gate pass PDF upload
              </span>
              <span className="hidden sm:block w-px h-12 bg-white/30" />
              <span className="flex items-center gap-4 text-white sm:text-lg">
                <i className="fas fa-truck-moving text-3xl pub-accent" />
                Carrier bid comparison
              </span>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-8">
              <button
                onClick={() => navigate('/dealer-register')}
                className="inline-flex items-center gap-3 px-10 py-4 rounded-lg pub-btn text-lg font-semibold transition"
              >
                Register as a dealer <i className="fas fa-arrow-right" />
              </button>
              <button
                onClick={() => navigate('/features#liveload')}
                className="text-lg text-white font-medium hover:opacity-80 transition"
              >
                How it works
              </button>
            </div>

            <div className="mt-12 pt-8 border-t border-white/15 grid grid-cols-3 max-w-3xl">
              {[['fa-file-arrow-up', 'Upload.'], ['fa-magnifying-glass', 'Compare.'], ['fa-play', 'Move.']].map(([icon, label], i) => (
                <div key={label} className={`flex flex-col items-center gap-3 ${i > 0 ? 'border-l border-white/20' : ''}`}>
                  <span className="w-12 h-12 rounded-full border-2 pub-accent-border flex items-center justify-center">
                    <i className={`fas ${icon} pub-accent`} />
                  </span>
                  <span className="text-lg font-semibold text-white">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pub-surface pub-line lg:border-l px-6 sm:px-10 py-14 flex flex-col justify-center">
          <div className="w-full max-w-md mx-auto">
            <div className="flex items-center justify-between gap-4">
              <p className="pub-text text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Your transport, at a glance</p>
              <span className="pub-soft shrink-0 px-3 py-1 rounded-full text-xs">Illustrative example</span>
            </div>

            <div className="pub-surface-2 pub-line border mt-6 rounded-xl shadow-sm p-6">
              <div className="pub-line flex items-center gap-4 pb-5 border-b">
                <span className="pub-soft w-16 h-14 rounded-lg flex items-center justify-center">
                  <i className="fas fa-car text-xl" />
                </span>
                <div>
                  <p className="pub-text text-lg font-bold">2024 BMW X5</p>
                  <p className="pub-faint">VIN: ***********7823</p>
                </div>
              </div>
              <div className="pub-line pub-muted py-4 border-b flex items-center gap-3">
                <i className="fas fa-location-dot pub-icon" /> Detroit, MI
                <i className="fas fa-arrow-right pub-faint" />
                <i className="fas fa-location-dot pub-icon" /> Columbus, OH
              </div>
              <p className="pub-muted mt-4">Posted budget</p>
              <p className="pub-link text-4xl font-bold">$450</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="pub-soft rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold">3</p>
                  <p className="pub-muted">carrier bids</p>
                </div>
                <div className="pub-soft rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold">$380</p>
                  <p className="pub-muted">Lowest bid</p>
                </div>
              </div>
            </div>

            <ol className="mt-8 space-y-6">
              {[
                ['Upload your gate pass', 'Send the PDF. We read the vehicle details from it.'],
                ['Compare carrier bids', 'See verified carriers and what each one charges.'],
                ['Pick a carrier', 'Choose one and your cars are on the way.'],
              ].map(([title, text], i) => (
                <li key={title} className="flex items-center gap-5">
                  <span className="pub-soft shrink-0 w-14 h-14 rounded-full text-xl font-semibold flex items-center justify-center">
                    0{i + 1}
                  </span>
                  <span>
                    <span className="pub-text block text-lg font-bold">{title}</span>
                    <span className="pub-muted block">{text}</span>
                  </span>
                </li>
              ))}
            </ol>

            <div className="pub-line pub-muted mt-8 pt-6 border-t text-center">
              Already registered?{' '}
              <button
                onClick={() => { setLoginMode('dealer'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="pub-link font-semibold"
              >
                Sign in <i className="fas fa-arrow-right ml-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Feature grid (design 3) ===== */}
      <FeatureGridSection />
    </PublicPage>
  );
};

export default HomePage;
